import { useEffect, useMemo } from 'react'

import { Shimmer } from '@wartek-id/shimmer'

import {
  useAPIGetNotificationList,
  useAPIMarkAllNotificationsAsRead,
} from 'api/kinerja/useAPIKinerjaNotification'
import {
  CardNotificationBase,
  CardNotificationPelaksanaanAppealGuru,
  CardNotificationPelaksanaanAppealKS,
  CardNotificationRotationMutation,
} from 'app/KinerjaNotifikasiPage/components/CardNotification'
import KinerjaSidebar from 'app/KinerjaShared/components/KinerjaSidebar'
import { GeneralNoticeState } from 'components/NoticeState'
import { assetUrl } from 'utils/url'
import { CardNotificationKsPelaksanaanAppealPs } from 'app/KinerjaNotifikasiPage/components/CardNotification/CardNotificationKsPelaksanaanAppealPs'
import { CardNotificationKsPelaksanaanAppealKs } from 'app/KinerjaNotifikasiPage/components/CardNotification/CardNotificationKsPelaksanaanAppealKs'
import { CardNotificationConfirmUnor } from 'app/KinerjaNotifikasiPage/components/CardNotification/CardNotificationConfirmUnor'
import { CardNotificationGeneral } from 'app/KinerjaNotifikasiPage/components/CardNotification/CardNotificationGeneral'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import useToggleSdkContext from 'utils/useToogleSdk'

import type { TPelaksanaanAppealableStep } from 'types/kinerja-pelaksanaan'
import type { PelaksanaanAppealableStep } from 'types/kinerja-ks-pelaksanaan'

function KinerjaNotifikasiIndex() {
  const {
    isPegawaiPengawasSekolah,
    isTimKinerjaPengawasSekolah,
    isPPKPengawasSekolah,
  } = useRoleContext()

  const toggleValue = useToggleSdkContext()
  const isNotifikasiPSPegawaiEnabled =
    toggleValue?.['kinerja-ps-pegawai-notification-enabled']

  const {
    data: dataNotificationList,
    isLoading,
    isSuccess: isSuccessNotificationList,
  } = useAPIGetNotificationList({
    options: {
      refetchOnMount: 'always',
    },
  })

  const markAllNotificationAsReadMutation = useAPIMarkAllNotificationsAsRead()

  const activeMenu = useMemo(() => {
    if (isNotifikasiPSPegawaiEnabled) {
      if (isPegawaiPengawasSekolah) {
        // PS as Tim Kinerja/Penilai KS
        return 'notifikasi-ps-tim-kinerja'
      }
      if (isTimKinerjaPengawasSekolah || isPPKPengawasSekolah) {
        // Tim Kinerja as Pegawai (if any)
        return 'notifikasi-ps-pegawai'
      }
    }
    return 'notifikasi'
  }, [
    isNotifikasiPSPegawaiEnabled,
    isPPKPengawasSekolah,
    isPegawaiPengawasSekolah,
    isTimKinerjaPengawasSekolah,
  ])

  useEffect(() => {
    if (isSuccessNotificationList) {
      markAllNotificationAsReadMutation.mutate({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessNotificationList])

  return (
    <div className="pl-64">
      <KinerjaSidebar sticky activeMenu={activeMenu} />

      <main className="py-8 pl-5 pr-8 space-y-4">
        {isLoading &&
          Array.from(Array(5).keys()).map((key) => (
            <Shimmer key={`shimmer-${key}`} width="100%" height="160px" />
          ))}

        {isSuccessNotificationList &&
          !isLoading &&
          dataNotificationList?.data?.length <= 0 && (
            <GeneralNoticeState
              containerClassNames="flex flex-col items-center justify-center mt-16"
              icon={
                <img
                  src={assetUrl('/illustrations/img-empty-document-2.png')}
                  alt=""
                  width="84"
                  height="84"
                />
              }
              title="Belum ada notifikasi"
              titleClassNames="!mt-3"
              description="Semua notifikasi sudah terbaca atau belum ada notifikasi baru. Mari cek yang lain?"
              descriptionClassNames="!mt-1"
            />
          )}
        {dataNotificationList?.data?.map((notificationItem) => {
          switch (notificationItem.type) {
            case 'APPEAL_REQUEST':
              return (
                <CardNotificationPelaksanaanAppealKS
                  key={notificationItem.id}
                  appealState={notificationItem.results?.confirmationState}
                  date={notificationItem.date}
                  description={notificationItem.description}
                  notificationId={notificationItem.id}
                  guruName={notificationItem.contents?.requestorPtkName}
                  step={
                    notificationItem.contents
                      ?.pelaksanaanStep as TPelaksanaanAppealableStep
                  }
                  title={notificationItem.title}
                />
              )
            case 'APPEAL_APPROVED':
              return (
                <CardNotificationPelaksanaanAppealGuru
                  key={notificationItem.id}
                  date={notificationItem.date}
                  description={notificationItem.description}
                  isApproved
                  step={
                    notificationItem.contents
                      ?.pelaksanaanStep as TPelaksanaanAppealableStep
                  }
                  title={notificationItem.title}
                />
              )
            case 'APPEAL_REJECTED':
              return (
                <CardNotificationPelaksanaanAppealGuru
                  key={notificationItem.id}
                  date={notificationItem.date}
                  description={notificationItem.description}
                  isApproved={false}
                  step={
                    notificationItem.contents
                      ?.pelaksanaanStep as TPelaksanaanAppealableStep
                  }
                  title={notificationItem.title}
                />
              )
            case 'ROTMUT_GURU_FOR_GURU':
            case 'ROTMUT_KS_EXIT_FOR_GURU':
            case 'ROTMUT_KS_JOIN_FOR_GURU':
            case 'ROTMUT_GURU_EXIT_FOR_KS':
            case 'ROTMUT_GURU_JOIN_FOR_KS':
            case 'ROTMUT_KS_FOR_KS':
            case 'ROTMUT_KS_PLT_FOR_KS':
            case 'ROTMUT_KS_PLT_FOR_GURU':
              return (
                <CardNotificationRotationMutation
                  key={notificationItem.id}
                  date={notificationItem.date}
                  description={notificationItem.description}
                  helpCenterUrl={notificationItem.contents?.helpCenterUrl}
                  title={notificationItem.title}
                />
              )

            // Kinerja KS
            case 'APPEAL_PELAKSANAAN_KS_REQUESTED':
              return (
                <CardNotificationKsPelaksanaanAppealPs
                  data-type={notificationItem.type}
                  key={notificationItem.id}
                  appealState={notificationItem.results?.confirmationState}
                  date={notificationItem.date}
                  description={notificationItem.description}
                  notificationId={notificationItem.id}
                  ksName={notificationItem.contents?.requestorPtkName}
                  step={
                    notificationItem.contents
                      ?.pelaksanaanStep as PelaksanaanAppealableStep
                  }
                  currentState={notificationItem.contents?.currentState}
                  title={notificationItem.title}
                  requestorNpsn={notificationItem.contents?.requestorNpsn || ''}
                  period={{
                    year: notificationItem?.contents?.year,
                    no: notificationItem?.contents?.period,
                  }}
                />
              )
            case 'APPEAL_PELAKSANAAN_KS_APPROVED':
            case 'APPEAL_PELAKSANAAN_KS_REJECTED':
              return (
                <CardNotificationKsPelaksanaanAppealKs
                  data-type={notificationItem.type}
                  key={notificationItem.id}
                  date={notificationItem.date}
                  description={notificationItem.description}
                  isApproved={
                    notificationItem.type === 'APPEAL_PELAKSANAAN_KS_APPROVED'
                  }
                  step={
                    notificationItem.contents
                      ?.pelaksanaanStep as PelaksanaanAppealableStep
                  }
                  title={notificationItem.title}
                  requestorNpsn={notificationItem.contents?.requestorNpsn || ''}
                />
              )

            case 'UNOR_CONFIRMATION':
              return (
                <CardNotificationConfirmUnor
                  key={notificationItem.id}
                  date={notificationItem.date}
                  description={notificationItem.description}
                  title={notificationItem.title}
                />
              )

            // For general use, with support of configureable multiple buttons
            case 'GENERAL':
              return (
                <CardNotificationGeneral
                  data-type={notificationItem.type}
                  key={notificationItem.id}
                  data={notificationItem}
                />
              )

            default:
              return (
                <CardNotificationBase
                  data-type={notificationItem.type}
                  key={notificationItem.id}
                  date={notificationItem.date}
                  description={notificationItem.description}
                  title={notificationItem.title}
                />
              )
          }
        })}
      </main>
    </div>
  )
}

export default KinerjaNotifikasiIndex
